import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { CartResponse } from 'types/mafia';
import type { PostPurchaseDataParams } from 'types/stripe';
import { configureCheckout } from 'apis/checkout';
import { isApplePayAvailable } from 'utils/applePay';
import { cn } from 'helpers/classnames';
import { toUSD } from 'helpers/NumberFormats';
import { removeTrailingZeroes } from 'helpers/ProductUtils';
import { FEATURE_CART_APPLE_PAY, KILLSWITCH_APPLE_PAY_CART } from 'constants/features';
import { CONFIGURE_CHECKOUT_SUCCESS } from 'store/ducks/checkout/types';
import { selectCheckoutData } from 'selectors/checkout';
import { useIsEnrolledInVariant } from 'hooks/unleash/useIsEnrolledInVariant';
import { useUnleashKillswitch } from 'hooks/unleash/useUnleashKillswitch';
import useMartyContext from 'hooks/useMartyContext';
import { selectCookies } from 'selectors/cookies';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { ApplePay } from 'components/ApplePay';

import css from 'styles/components/cart/cartCheckout.scss';

interface Props {
  itemCountVerbiage: string;
  taxVerbiage: string;
  cart: CartResponse;
  onCheckingOut: (...args: any[]) => void;
  loading: boolean;
  isRecognizedCustomer: boolean;
}

export const CartCheckout = ({ itemCountVerbiage, taxVerbiage, cart, onCheckingOut, loading, isRecognizedCustomer }: Props) => {
  const { testId, marketplace } = useMartyContext();
  const { purchase: purchaseState } = useSelector(selectCheckoutData);
  const cookies = useSelector(selectCookies);
  const dispatch = useDispatch();
  const { isEnrolled: isApplePayEnrolled } = useIsEnrolledInVariant({ featureFlag: FEATURE_CART_APPLE_PAY, isZapposOnly: false });
  const { isKillswitchOn: isApplePayDisabled } = useUnleashKillswitch({ featureFlag: KILLSWITCH_APPLE_PAY_CART, isZapposOnly: false });

  const {
    api: { mafia },
    cart: { cartName, cartCodeVerbiage },
    checkout: { stripeApiKey }
  } = marketplace;

  const {
    activeItemTotalQuantity,
    subtotal: { amount }
  } = cart;

  const isApplePayEnabled = isApplePayAvailable() && !isApplePayDisabled && isRecognizedCustomer && isApplePayEnrolled;
  const hasPurchase = !!Object.keys(purchaseState).length;

  const loadPurchase = useCallback(async () => {
    const { payload } = await configureCheckout({ url: mafia.url }, { reqData: { useDiscount: true, useGCBalance: true } }, cookies);
    const { purchaseStatus } = payload;

    dispatch({ type: CONFIGURE_CHECKOUT_SUCCESS, payload: { purchaseStatus } });
  }, []);

  const updatePurchase = useCallback(async (reqData: PostPurchaseDataParams) => {
    const response = await configureCheckout({ url: mafia.url }, { reqData }, cookies);
    const { purchaseStatus } = response.payload;

    dispatch({ type: CONFIGURE_CHECKOUT_SUCCESS, payload: { purchaseStatus } });

    return purchaseStatus;
  }, []);

  useEffect(() => {
    if (isApplePayEnabled && !loading && !hasPurchase) {
      loadPurchase();
    }
  }, [isApplePayEnabled, loading, hasPurchase]);

  return (
    <div className={css.container} data-test-id={testId('cartCheckout')}>
      <button
        className="zappos:leading-none"
        type="button"
        disabled={loading || !activeItemTotalQuantity}
        onClick={onCheckingOut}
        data-test-id={testId('proceedToCheckout')}
      >
        {loading ? 'Updating...' : 'Proceed to Checkout'}
      </button>
      {isApplePayEnabled && hasPurchase && (
        <div className="order-2 mb-1 mt-3 h-10 md:order-none">
          <ApplePay apiKey={stripeApiKey} isGuest={false} purchaseState={purchaseState} theme="cart" updatePurchase={updatePurchase} />
        </div>
      )}

      <p className="zappos:body-xs">{cartCodeVerbiage}</p>

      <h3 className="zappos:body-l-medium">
        {cartName} summary ({itemCountVerbiage})
      </h3>

      <dl className={css.subtotal}>
        <dt className="zappos:body-m-bold" data-test-id={testId('subtotalLabel')}>
          Subtotal<span> ({itemCountVerbiage})</span>
        </dt>
        <dd className="zappos:body-m-bold" data-test-id={testId('subtotal')}>
          {removeTrailingZeroes(toUSD(amount))}
        </dd>
      </dl>
      <span className={cn(css.legal, 'zappos:body-xs')} dangerouslySetInnerHTML={{ __html: taxVerbiage }} data-test-id={testId('taxVerbiage')} />
    </div>
  );
};

const WithErrorBoundaryCartCheckout = withErrorBoundary('CartCheckout', CartCheckout);
export default WithErrorBoundaryCartCheckout;
